import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Input, Button, Checkbox, Select } from 'antd';

import { getProposalTitle } from 'helpers/proposal';
import { userVar, fromAppVar } from 'graphql/cache';
import utils from 'utils/utils';
import {
  ClientName,
  WixClientName,
  MondayClientName,
  HubspotClientName,
  PipedriveClientName,
  PipedriveDealName,
} from 'pages/Proposal/modules';
import CloseIcon from 'components/Icons/CloseIcon';
import DropdownIcon from 'components/Icons/DropdownIcon';

import './CreateDuplicateProposalModal.scss';

const CreateDuplicateProposalModal = ({
  user,
  visible,
  onCancel,
  selectedProposal,
  onConfirm,
  modalType,
  isLoading,
  isFromTemplate,
  dontOverWriteHeader,
  setDontOverWriteHeader,
  dealId,
  dealClientName,
  dealClientEmail,
}) => {
  const userVariables = userVar();
  const proposalTitle =
    selectedProposal?.templateName || getProposalTitle({ proposal: selectedProposal });

  const [clientName, setClientName] = useState(dealClientName || '');
  const [clientEmail, setClientEmail] = useState(dealClientEmail || '');
  const [clientCompany, setClientCompany] = useState('');
  const [mondayItemId, setMondayItemId] = useState('');
  const [hubspotContactId, setHubspotContactId] = useState('');
  const [pipedriveDealId, setPipedriveDealId] = useState(dealId || '');
  const [selectedIntegration, setSelectedIntegration] = useState('');

  const [form] = Form.useForm();

  const onInputChange = (
    value,
    email,
    company,
    mondayItemId,
    pipedrivesDealName,
    pipedrivesDealId,
    hubspotContactId
  ) => {
    if (value) {
      const limitedValue = utils.limitInputLength(value);
      setClientName(limitedValue);
    }
    if (email) {
      setClientEmail(email);
    }
    if (company) {
      setClientCompany(company);
    }
    if (mondayItemId) {
      setMondayItemId(mondayItemId);
    }
    if (hubspotContactId) {
      setHubspotContactId(hubspotContactId);
    }

    if (pipedrivesDealId) {
      form.setFieldsValue({ pipedrive_deal_id: pipedrivesDealId });
      setPipedriveDealId(pipedrivesDealName);
    }
  };

  const onCreateDuplicateClick = () => {
    form
      .validateFields()
      .then((values) => {
        values = {
          ...values,
          client_email: clientEmail,
          client_company: clientCompany,
          monday_item_id: mondayItemId,
          hubspot_contact_id: hubspotContactId,
        };
        onConfirm({ values, proposal: selectedProposal });
      })
      .catch((info) => {
        console.error('Validate Failed:', info);
      });
  };

  const language =
    selectedProposal?.language === 'hebrew'
      ? 'hebrew'
      : selectedProposal?.language === 'arabic'
      ? 'arabic'
      : '';

  // Get available integrations
  const getAvailableIntegrations = () => {
    const integrations = [];

    if (userVariables?.pipedrive) {
      integrations.push({ value: 'pipedrive', label: 'Pipedrive' });
    }

    if (Object.keys(userVariables?.monday || {})?.length > 0) {
      integrations.push({ value: 'monday', label: 'Monday.com' });
    }

    if (userVariables.hubspot?.token?.expires_in) {
      integrations.push({ value: 'hubspot', label: 'Hubspot' });
    }

    if (userVariables?.wix?.length) {
      integrations.push({ value: 'wix', label: 'Wix' });
    }

    return integrations;
  };

  const availableIntegrations = getAvailableIntegrations();

  // Auto-select integration
  useEffect(() => {
    if (availableIntegrations?.length && !selectedIntegration) {
      if (fromAppVar() === 'hubspot') {
        setSelectedIntegration('hubspot');
        form.setFieldsValue({ integration: 'hubspot' });
      } else if (fromAppVar() === 'monday') {
        setSelectedIntegration('monday');
        form.setFieldsValue({ integration: 'monday' });
      }
      // Allow users to create without selecting integration
    }
  }, [availableIntegrations, form, selectedIntegration]);

  return (
    <Modal
      className={`simple-info-modal create-duplicate-modal ${language}`}
      visible={visible}
      okText="CONTINUE"
      centered
      cancelText="CANCEL"
      closeIcon={<CloseIcon className="close-icon" />}
      footer={null}
      onCancel={onCancel}>
      <h3 className="title">{modalType} Proposal</h3>
      <div className="divider" />

      <div className="body">
        <Form
          form={form}
          layout="vertical"
          name="form_in_modal"
          className="form"
          initialValues={{
            modifier: 'public',
            project_name: `${proposalTitle}-COPY`,
            client_name: clientName,
            pipedrive_deal_id: pipedriveDealId || '',
          }}>
          <Form.Item
            name="project_name"
            label="Project Name"
            rules={[
              {
                required: true,
                message: 'Project name is required.',
              },
              {
                whitespace: true,
                message: 'Project name is required.',
              },
            ]}>
            <Input />
          </Form.Item>
          {availableIntegrations?.length >= 1 && (
            <Form.Item label="Select Integration (Optional)" name="integration">
              <div className="create-duplicate-integration-selector">
                <Select
                  placeholder="Choose an integration or leave blank"
                  className="wizard-integration-selector-dropdown"
                  value={selectedIntegration}
                  onChange={(value) => {
                    setSelectedIntegration(value);
                    form.setFieldsValue({ integration: value });
                  }}
                  allowClear
                  onClear={() => {
                    setSelectedIntegration('');
                    form.setFieldsValue({ integration: undefined });
                  }}
                  options={availableIntegrations}
                  suffixIcon={<DropdownIcon />}
                />
              </div>
            </Form.Item>
          )}

          <Form.Item
            name="client_name"
            label="Prospect Name"
            rules={[
              {
                required: true,
                message: 'Prospect name is required',
              },
              {
                whitespace: true,
                message: 'Prospect name is required.',
              },
            ]}>
            {selectedIntegration === 'monday' ? (
              <MondayClientName
                type="setContact"
                action="contact"
                clientNameValue={''}
                alert={true}
                onChange={onInputChange}
                fromTemplate={true}
              />
            ) : selectedIntegration === 'wix' ? (
              <WixClientName
                type="setContact"
                action="contact"
                clientNameValue={''}
                onChange={onInputChange}
                fromTemplate={true}
                language={language}
              />
            ) : selectedIntegration === 'pipedrive' ? (
              <PipedriveClientName
                type="setContact"
                action="contact"
                clientNameValue={clientName}
                onChange={onInputChange}
                fromTemplate={true}
                language={language}
              />
            ) : selectedIntegration === 'hubspot' ? (
              <HubspotClientName
                type="setContact"
                action="contact"
                clientNameValue={clientName}
                onChange={onInputChange}
                fromTemplate={true}
                language={language}
              />
            ) : (
              <ClientName
                type="setContact"
                action="contact"
                onChange={onInputChange}
                placeholder="CONTACT PERSON NAME"
                fromTemplate={true}
                language={language}
              />
            )}
          </Form.Item>

          {selectedIntegration === 'pipedrive' && (
            <Form.Item name="pipedrive_deal_id" label="Deal">
              <PipedriveDealName
                type="setDeal"
                action="deal"
                dealNameValue={pipedriveDealId}
                onChange={onInputChange}
                fromTemplate={true}
                language={language}
              />
            </Form.Item>
          )}

          {isFromTemplate && (
            <Form.Item
              name="replace_header_signature"
              className="dont-replace-header-checkbox-wrapper">
              <Checkbox
                className="dont-replace-header-checkbox"
                value={dontOverWriteHeader}
                onChange={() => setDontOverWriteHeader(!dontOverWriteHeader)}>
                Do not replace headers
              </Checkbox>
            </Form.Item>
          )}
        </Form>
      </div>

      <div className="button-wrapper">
        <Button
          block
          className="large-btn secondary-btn"
          loading={isLoading}
          onClick={onCreateDuplicateClick}>
          {modalType}
        </Button>
        <Button block className="large-btn grey-btn" disabled={isLoading} onClick={onCancel}>
          CANCEL
        </Button>
      </div>
    </Modal>
  );
};

CreateDuplicateProposalModal.defaultProps = {
  visible: false,
  isLoading: false,
};

CreateDuplicateProposalModal.propTypes = {
  isLoading: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  visible: PropTypes.bool,
  selectedProposal: PropTypes.shape({}).isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default CreateDuplicateProposalModal;
